/**
 * The flows behind "Make this flow yours" on www.flowgate.co.uk: each one is the playable demo
 * a visitor just used (landing-page/src/data/simFlows.js), screen for screen, so the flow they
 * land on in the builder is the one they tried. landing-page's demo parity test compares them.
 *
 * How the demo maps onto real Flow JSON:
 * - each demo step is a screen, titled like the demo's sheet, with its button label;
 * - a choice is a RadioButtonsGroup whose option ids are the readable answer ("Cleaning & polish
 *   (30 min)"), so the review screen can show it;
 * - times that depend on an earlier choice are fixed per option and swapped by an If. With booking
 *   connected they come live from a diary; a template has no diary behind it;
 * - the review screen shows answers as ${data.x}, handed over by each Continue's payload.
 *   WhatsApp does not fill ${form.x} or ${screen.X.form.x} in visible text, and an uploaded file
 *   cannot travel in a navigate payload, so files are read in the final complete payload instead;
 * - no Switch: Meta's validator crashes on Switch case keys with spaces or brackets (2026-10-09),
 *   and these keys are readable answers. Nested Ifs instead.
 *
 * Every one was checked against Meta's validator (evals/meta-validate.sh) on 2026-10-09.
 */
const nav = (next, payload = {}) => ({ type: 'Footer', label: 'Continue', 'on-click-action': { name: 'navigate', next: { type: 'screen', name: next }, payload } });
/** What a screen is handed by the Continue before it, so a review screen can show the answers. */
const data = (examples) => Object.fromEntries(Object.entries(examples).map(([k, v]) => [k, { type: 'string', __example__: v }]));
const done = (label, payload) => ({ type: 'Footer', label, 'on-click-action': { name: 'complete', payload } });
const radio = (name, label, options) => ({
  type: 'RadioButtonsGroup', name, label, required: true,
  'data-source': options.map(([id, title, description]) => (description ? { id, title, description } : { id, title })),
});
const text = (name, label, inputType = 'text', required = true) => ({ type: 'TextInput', name, label, 'input-type': inputType, required });
const row = (caption, value) => [{ type: 'TextCaption', text: caption }, { type: 'TextBody', text: value }];
const screen = (id, title, children, terminal = false, declared = null) => ({ id, title, ...(terminal ? { terminal: true } : {}), ...(declared ? { data: declared } : {}), layout: { type: 'SingleColumnLayout', children } });

export const clinic = {
  version: '7.3',
  screens: [
    screen('screen_a', 'Book an appointment', [
      radio('service', 'Service', [
        ['Cleaning & polish (30 min)', 'Cleaning & polish', '30 min'],
        ['Check-up (20 min)', 'Check-up', '20 min'],
        ['Teeth whitening (60 min)', 'Teeth whitening', '60 min'],
      ]),
      { type: 'DocumentPicker', name: 'referral', label: 'Referral letter', 'min-uploaded-documents': 0, 'max-uploaded-documents': 1 },
      nav('screen_b', { service: '${form.service}' }),
    ]),
    screen('screen_b', 'Book an appointment', [
      radio('practitioner', 'Practitioner', [['Dr. Patel · Hygienist', 'Dr. Patel', 'Hygienist'], ['Dr. Reyes · Dentist', 'Dr. Reyes', 'Dentist']]),
      { type: 'If', condition: "${form.practitioner} == 'Dr. Reyes · Dentist'",
        then: [radio('time_reyes', 'Thu 12 Jun', [['Thu 12 Jun · 10:15', '10:15'], ['Thu 12 Jun · 13:00', '13:00'], ['Thu 12 Jun · 16:45', '16:45']])],
        else: [radio('time_patel', 'Thu 12 Jun', [['Thu 12 Jun · 09:30', '09:30'], ['Thu 12 Jun · 11:00', '11:00'], ['Thu 12 Jun · 14:30', '14:30']])] },
      nav('screen_c', { service: '${data.service}', practitioner: '${form.practitioner}', time_patel: '${form.time_patel}', time_reyes: '${form.time_reyes}' }),
    ], false, data({ service: 'Cleaning & polish (30 min)' })),
    screen('screen_c', 'Book an appointment', [
      { type: 'TextSubheading', text: 'Your booking' },
      ...row('Service', '${data.service}'),
      ...row('With', '${data.practitioner}'),
      { type: 'TextCaption', text: 'When' },
      { type: 'If', condition: "${data.practitioner} == 'Dr. Reyes · Dentist'",
        then: [{ type: 'TextBody', text: '${data.time_reyes}' }],
        else: [{ type: 'TextBody', text: '${data.time_patel}' }] },
      done('Confirm booking', {
        service: '${data.service}',
        referral: '${screen.screen_a.form.referral}',
        practitioner: '${data.practitioner}',
        time_patel: '${data.time_patel}',
        time_reyes: '${data.time_reyes}',
      }),
    ], true, data({ service: 'Cleaning & polish (30 min)', practitioner: 'Dr. Patel · Hygienist', time_patel: 'Thu 12 Jun · 11:00', time_reyes: '' })),
  ],
};

export const cafe = {
  version: '7.3',
  screens: [
    screen('screen_a', 'How was your visit?', [
      radio('rating', 'Your visit today', [['Excellent', 'Excellent'], ['Good', 'Good'], ['Okay', 'Okay'], ['Poor', 'Poor']]),
      nav('screen_b'),
    ]),
    screen('screen_b', 'How was your visit?', [
      { type: 'TextSubheading', text: 'Anything we should know?' },
      { type: 'TextArea', name: 'comments', label: 'Comments', required: false },
      done('Submit feedback', { rating: '${screen.screen_a.form.rating}', comments: '${form.comments}' }),
    ], true),
  ],
};

export const kitchen = {
  version: '7.3',
  screens: [
    screen('screen_a', 'Get a tailored quote', [
      radio('project', 'Project', [
        ['Full renovation · from £12k', 'Full renovation', 'from £12k'],
        ['Worktops only · from £2.4k', 'Worktops only', 'from £2.4k'],
        ['Cabinet refresh · from £4.1k', 'Cabinet refresh', 'from £4.1k'],
      ]),
      nav('screen_b', { project: '${form.project}' }),
    ]),
    screen('screen_b', 'Get a tailored quote', [
      { type: 'TextSubheading', text: 'Your details' },
      text('full_name', 'Name'),
      text('email', 'Email', 'email'),
      nav('screen_c', { project: '${data.project}', full_name: '${form.full_name}', email: '${form.email}' }),
    ], false, data({ project: 'Full renovation · from £12k' })),
    screen('screen_c', 'Get a tailored quote', [
      { type: 'TextSubheading', text: 'Your request' },
      ...row('Project and estimate', '${data.project}'),
      ...row('Contact', '${data.full_name}'),
      done('Request quote', { project: '${data.project}', full_name: '${data.full_name}', email: '${data.email}' }),
    ], true, data({ project: 'Full renovation · from £12k', full_name: 'Alex Carter', email: 'alex@example.com' })),
  ],
};

export const viewing = {
  version: '7.3',
  screens: [
    screen('screen_a', 'Book a viewing', [
      radio('day', '3-bed house, Crookes', [['Thursday', 'Thursday'], ['Friday', 'Friday']]),
      { type: 'If', condition: "${form.day} == 'Thursday'",
        then: [radio('time_thursday', 'Viewing times', [['Thursday 10:00', '10:00'], ['Thursday 12:30', '12:30'], ['Thursday 17:30', '17:30']])],
        else: [radio('time_friday', 'Viewing times', [['Friday 09:30', '09:30'], ['Friday 13:00', '13:00'], ['Friday 18:00', '18:00']])] },
      nav('screen_b', { day: '${form.day}', time_thursday: '${form.time_thursday}', time_friday: '${form.time_friday}' }),
    ]),
    screen('screen_b', 'Book a viewing', [
      { type: 'TextSubheading', text: 'Your details' },
      text('full_name', 'Name'),
      text('mobile', 'Mobile', 'phone'),
      nav('screen_c', { day: '${data.day}', time_thursday: '${data.time_thursday}', time_friday: '${data.time_friday}', full_name: '${form.full_name}', mobile: '${form.mobile}' }),
    ], false, data({ day: 'Thursday', time_thursday: 'Thursday 12:30', time_friday: '' })),
    screen('screen_c', 'Book a viewing', [
      { type: 'TextSubheading', text: 'Your viewing' },
      ...row('Property', '3-bed house, Crookes'),
      { type: 'TextCaption', text: 'When' },
      { type: 'If', condition: "${data.day} == 'Thursday'",
        then: [{ type: 'TextBody', text: '${data.time_thursday}' }],
        else: [{ type: 'TextBody', text: '${data.time_friday}' }] },
      ...row('Viewer', '${data.full_name}'),
      done('Book viewing', { day: '${data.day}', time_thursday: '${data.time_thursday}', time_friday: '${data.time_friday}', full_name: '${data.full_name}', mobile: '${data.mobile}' }),
    ], true, data({ day: 'Thursday', time_thursday: 'Thursday 12:30', time_friday: '', full_name: 'Sam Hughes', mobile: '07700 900123' })),
  ],
};

export const garage = {
  version: '7.3',
  screens: [
    screen('screen_a', 'Book your car in', [
      radio('job', 'What does it need?', [['MOT (45 min)', 'MOT', '45 min'], ['Full service (2 hrs)', 'Full service', '2 hrs'], ['MOT and service (3 hrs)', 'MOT and service', '3 hrs']]),
      // Ifs, not a Switch: Meta's validator crashes on Switch case keys with spaces or brackets
      // ("MOT (45 min)"), and these keys are the readable answers the review screen shows.
      { type: 'If', condition: "${form.job} == 'MOT (45 min)'",
        then: [radio('drop_off_mot', 'Monday drop-off', [['Monday 08:00', '08:00'], ['Monday 10:30', '10:30'], ['Monday 13:30', '13:30']])],
        else: [{ type: 'If', condition: "${form.job} == 'Full service (2 hrs)'",
          then: [radio('drop_off_service', 'Monday drop-off', [['Monday 08:00', '08:00'], ['Monday 09:00', '09:00']])],
          else: [radio('drop_off_both', 'Monday drop-off', [['Monday 08:00', '08:00'], ['Monday 08:30', '08:30']])] }] },
      nav('screen_b', { job: '${form.job}', drop_off_mot: '${form.drop_off_mot}', drop_off_service: '${form.drop_off_service}', drop_off_both: '${form.drop_off_both}' }),
    ]),
    screen('screen_b', 'Book your car in', [
      { type: 'TextSubheading', text: 'Your car' },
      text('registration', 'Registration'),
      text('make_model', 'Make and model'),
      { type: 'PhotoPicker', name: 'photo', label: 'Any warning lights?', 'photo-source': 'camera_gallery', 'min-uploaded-photos': 0, 'max-uploaded-photos': 1 },
      nav('screen_c', { job: '${data.job}', drop_off_mot: '${data.drop_off_mot}', drop_off_service: '${data.drop_off_service}', drop_off_both: '${data.drop_off_both}', registration: '${form.registration}', make_model: '${form.make_model}' }),
    ], false, data({ job: 'MOT (45 min)', drop_off_mot: 'Monday 10:30', drop_off_service: '', drop_off_both: '' })),
    screen('screen_c', 'Book your car in', [
      { type: 'TextSubheading', text: 'Your booking' },
      ...row('Job', '${data.job}'),
      { type: 'TextCaption', text: 'Drop-off' },
      { type: 'If', condition: "${data.job} == 'MOT (45 min)'",
        then: [{ type: 'TextBody', text: '${data.drop_off_mot}' }],
        else: [{ type: 'If', condition: "${data.job} == 'Full service (2 hrs)'",
          then: [{ type: 'TextBody', text: '${data.drop_off_service}' }],
          else: [{ type: 'TextBody', text: '${data.drop_off_both}' }] }] },
      ...row('Car', '${data.registration}'),
      done('Book it in', {
        job: '${data.job}', drop_off_mot: '${data.drop_off_mot}', drop_off_service: '${data.drop_off_service}', drop_off_both: '${data.drop_off_both}',
        registration: '${data.registration}', make_model: '${data.make_model}', photo: '${screen.screen_b.form.photo}',
      }),
    ], true, data({ job: 'MOT (45 min)', drop_off_mot: 'Monday 10:30', drop_off_service: '', drop_off_both: '', registration: 'AB12 CDE', make_model: 'Ford Focus' })),
  ],
};

export const homeQuote = {
  version: '7.3',
  screens: [
    screen('screen_a', 'Request a visit', [
      radio('problem', 'What’s wrong?', [['No heating', 'No heating'], ['No hot water', 'No hot water'], ['Something’s leaking', 'Something’s leaking'], ['Annual service', 'Annual service']]),
      nav('screen_b', { problem: '${form.problem}' }),
    ]),
    screen('screen_b', 'Request a visit', [
      { type: 'PhotoPicker', name: 'photos', label: 'Photos of the boiler', 'photo-source': 'camera_gallery', 'min-uploaded-photos': 1, 'max-uploaded-photos': 2 },
      { type: 'TextSubheading', text: 'Where and who' },
      text('full_name', 'Name'),
      text('postcode', 'Postcode'),
      nav('screen_c', { problem: '${data.problem}', full_name: '${form.full_name}', postcode: '${form.postcode}' }),
    ], false, data({ problem: 'No heating' })),
    screen('screen_c', 'Request a visit', [
      { type: 'TextSubheading', text: 'Your request' },
      ...row('Problem', '${data.problem}'),
      ...row('Postcode', '${data.postcode}'),
      ...row('Photos', 'Attached'),
      done('Send request', { problem: '${data.problem}', photos: '${screen.screen_b.form.photos}', full_name: '${data.full_name}', postcode: '${data.postcode}' }),
    ], true, data({ problem: 'No heating', full_name: 'Priya Shah', postcode: 'S10 2AB' })),
  ],
};
